import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface LabelCreationFormData {
  // Label Details
  labelName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  ddi?: string;
  ddiCountryCode?: string; // Store country code for flag restoration
  phoneMask?: string; // Store phone mask for input restoration
  phoneNumber?: string;
  birthDate?: string;

  // Label Access (excluding sensitive password data)
  username?: string;

  // Form state
  currentStep?: string;
}

export interface LabelFormControlStates {
  // Track which fields have been interacted with
  labelName?: { touched: boolean; dirty: boolean };
  firstName?: { touched: boolean; dirty: boolean };
  lastName?: { touched: boolean; dirty: boolean };
  email?: { touched: boolean; dirty: boolean };
  ddi?: { touched: boolean; dirty: boolean };
  phoneNumber?: { touched: boolean; dirty: boolean };
  birthDate?: { touched: boolean; dirty: boolean };
  username?: { touched: boolean; dirty: boolean };
}

@Injectable({
  providedIn: 'root',
})
export class MzicLabelCreationFormStateService {
  private labelImageSource = new BehaviorSubject<string | null>(null);
  public labelImage$ = this.labelImageSource.asObservable();

  private imageIdSource = new BehaviorSubject<string | null>(null);
  public imageId$ = this.imageIdSource.asObservable();

  private labelFormDataSource = new BehaviorSubject<LabelCreationFormData>({});
  public labelFormData$ = this.labelFormDataSource.asObservable();

  private currentStepSource = new BehaviorSubject<string>('initial');
  public currentStep$ = this.currentStepSource.asObservable();

  private controlStatesSource = new BehaviorSubject<LabelFormControlStates>({});
  public controlStates$ = this.controlStatesSource.asObservable();

  constructor() {
    this.initStateFromStorage();
  }

  private initStateFromStorage(): void {
    try {
      const savedImage = localStorage.getItem('label_creation_form_image');
      const savedImageId = localStorage.getItem('label_creation_form_image_id');
      const savedFormData = localStorage.getItem(
        'label_creation_form_data_new',
      );
      const savedStep = localStorage.getItem('label_creation_form_step');
      const savedControlStates = localStorage.getItem(
        'label_creation_form_control_states',
      );

      if (savedImage) {
        this.labelImageSource.next(savedImage);
      }

      if (savedImageId) {
        this.imageIdSource.next(savedImageId);
      }

      if (savedFormData) {
        this.labelFormDataSource.next(JSON.parse(savedFormData));
      }

      if (savedStep) {
        this.currentStepSource.next(savedStep);
      }

      if (savedControlStates) {
        this.controlStatesSource.next(JSON.parse(savedControlStates));
      }
    } catch (error) {
      console.error(
        'Error recovering label form state from localStorage',
        error,
      );
      this.clearState();
    }
  }

  setLabelImage(imageUrl: string): void {
    this.labelImageSource.next(imageUrl);
    localStorage.setItem('label_creation_form_image', imageUrl);
  }

  setImageId(imageId: string): void {
    this.imageIdSource.next(imageId);
    localStorage.setItem('label_creation_form_image_id', imageId);
  }

  getLabelImage(): string | null {
    return this.labelImageSource.value;
  }

  getImageId(): string | null {
    return this.imageIdSource.value;
  }

  updateLabelFormData(data: Partial<LabelCreationFormData>): void {
    const currentData = this.labelFormDataSource.value;
    const updatedData = { ...currentData, ...data };
    this.labelFormDataSource.next(updatedData);
    localStorage.setItem(
      'label_creation_form_data_new',
      JSON.stringify(updatedData),
    );
  }

  getLabelFormData(): LabelCreationFormData {
    return this.labelFormDataSource.value;
  }

  setCurrentStep(step: string): void {
    this.currentStepSource.next(step);
    localStorage.setItem('label_creation_form_step', step);
  }

  getCurrentStep(): string {
    return this.currentStepSource.value;
  }

  updateControlStates(states: Partial<LabelFormControlStates>): void {
    const currentStates = this.controlStatesSource.value;
    const updatedStates = { ...currentStates, ...states };
    this.controlStatesSource.next(updatedStates);
    localStorage.setItem(
      'label_creation_form_control_states',
      JSON.stringify(updatedStates),
    );
  }

  getControlStates(): LabelFormControlStates {
    return this.controlStatesSource.value;
  }

  clearState(): void {
    this.labelImageSource.next(null);
    this.imageIdSource.next(null);
    this.labelFormDataSource.next({});
    this.currentStepSource.next('initial');
    this.controlStatesSource.next({});
    localStorage.removeItem('label_creation_form_image');
    localStorage.removeItem('label_creation_form_image_id');
    localStorage.removeItem('label_creation_form_data_new');
    localStorage.removeItem('label_creation_form_step');
    localStorage.removeItem('label_creation_form_control_states');
  }

  /**
   * Clears all registration data when going back from profile setup (first step)
   * This provides a complete fresh start for the registration process
   */
  clearNameAndImage(): void {
    // Since profile setup is the first step, clear ALL registration data
    // This ensures users get a completely fresh start when going back
    this.clearState();
  }

  hasPersistedData(): boolean {
    const formData = this.getLabelFormData();
    return Object.keys(formData).length > 0 || !!this.getLabelImage();
  }
}
